import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'
import { format } from 'pretty-format'

import * as node from '../../node'
import type { InstanceHelper } from '../helper'
import type { Key } from '../key'
import type { Type } from '../type'

/**
 * Generates a date instance. It can accept either a date string, an object with
 * date parts, or a special selector.
 *
 * @example
 * // Using a date string:
 * { '$date': "05/27/2025" }
 *
 * // Using a date instance:
 * { '$date': new Date() }
 *
 * // Using date parts (day, month, year):
 * { '$date': { day: 27, month: 5, year: 2025 } }
 *
 * // Using a special selector keyword:
 * { '$date': "today" }
 */
export type Date<out T = any> = Readonly<
  Record<Key<Type.Date>, T extends any ? Date.Params : never>
>
export namespace Date {
  export const isDate: TypeChecker<Date> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Date>({
      $date: Params.isParams,
    }),
  ).usingAlias('Instance.Date')
  export const assertIsDate: TypeAsserter<Date> =
    TypeAsserter.Factory.from(isDate)

  /**
   * Parameters that can be passed to create a Date instance
   *
   * @see {@link Date}
   */
  export type Params = string | globalThis.Date | Values | Selector
  export namespace Params {
    export const isParams: TypeChecker<Params> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.union(
        TypeChecker.isString,
        TypeChecker.isDate,
        Values.isValues,
        Selector.isSelector,
      ),
    )
  }

  export const HELPER: InstanceHelper<Date> = {
    createNode: ({ $date: arg }) => {
      let date: DmsDate | undefined

      if (TypeChecker.isDate(arg)) {
        date = new DmsDate(arg)
      }
      if (Selector.isSelector(arg)) {
        // NOTE: this must be first since `Selector` is also a `string`
        switch (arg) {
          // Creates a current date
          case 'today':
            // TODO: - Handle different time zones
            date = new DmsDate()
            break
          default:
            throw TypeAsserter.assertIsNever(arg)
        }
      } else if (TypeChecker.isString(arg)) {
        // NOTE: If the argument is a string, we use the default JS parsing
        // logic
        date = new DmsDate(arg)
      } else if (Values.isValues(arg)) {
        const { year, month, day } = arg

        // NOTE: The month in JavaScript Date is zero-based (0 = January)
        date = new DmsDate(
          year ?? new globalThis.Date().getFullYear(), // Provided year or current year
          month ? month - 1 : 0, // Provided month (adjusted to 0-based) or January
          day ?? 1, // Provided day or the 1st
        )
      }

      return new node.ForValue(date)
    },
    // NOTE: Date.Values ({ year, month, day }) and Date.Selector ("today") are
    // expression-time constructs evaluated in createNode. They don't appear in
    // serialized data — toJson always produces ISO strings via DmsDate.toJSON().
    fromPlain: ({ $date: arg }): DmsDate | undefined => {
      if (TypeChecker.isString(arg)) {
        const date = new DmsDate(arg)
        return isNaN(date.valueOf()) ? undefined : date
      }
      if (TypeChecker.isDate(arg)) {
        return new DmsDate(arg)
      }
      return undefined
    },
    runtimeTypeChecker: TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.instance(DmsDate),
    ),
    typeChecker: isDate,
  }

  /**
   * Special keywords that can be used to generate dates
   */
  export type Selector = 'today'
  export namespace Selector {
    export const isSelector: TypeChecker<Selector> = TypeChecker.Factory.oneOf([
      'today',
    ])
  }

  /**
   * An object that represents parts of a date. All fields are optional -
   * missing fields will use default values
   */
  export type Values = Partial<{
    /**
     * @default current year
     */
    year: number
    /**
     *
     * @default 1
     */
    month: number
    /**
     * @default 1
     */
    day: number
  }>
  export namespace Values {
    export const isValues: TypeChecker<Values> = TypeChecker.Factory.object({
      day: TypeChecker.Factory.optional(TypeChecker.isNumber),
      month: TypeChecker.Factory.optional(TypeChecker.isNumber),
      year: TypeChecker.Factory.optional(TypeChecker.isNumber),
    })
  }
}

/**
 * Same as {@link globalThis.Date} but with custom {@link DmsDate.toJSON} which
 * returns a DMS {@link Date} expression
 */
export class DmsDate extends globalThis.Date {
  // NOTE: - Using 'any' return type because TypeScript's Date.toJSON()
  // signature only returns string, but we need a DMS instance format which is
  // an object
  public override toJSON(): any {
    return { $date: super.toJSON() } satisfies Date
  }
}
